/*
 * Photo Headshot — DeckorateAPI client.
 *
 * Talks to POST /v1/deckorate/image/photo-head. Full path used since
 * config.deckorateApiBaseUrl carries no /v1/deckorate segment — same
 * convention insightsApi.ts uses for /v1/deckorate/content/get-insights.
 * The endpoint downloads the referenced blob server-side and runs one
 * face-detection model call; it does not accept inline base64, so the
 * caller must have already placed the photo at
 * `image_blob_container`/`image_blob_name` (see photoHeadUpload.ts) before
 * calling this.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { FaceBox } from "../../../types";
import {
  createConnectorLogger,
  getErrorMessage,
  isAbortError,
  parseHttpError,
  parseJsonErrorBody,
  HTTP_STATUS,
} from "../../shared/services/connectorErrors";

export { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";

const PHOTO_HEAD_CONNECTOR = "deckorate";
const PHOTO_HEAD_DETECT_PATH = "/v1/deckorate/image/photo-head";

/** How long to wait for the face-detection call before giving up on it. */
export const PHOTO_HEAD_REQUEST_TIMEOUT_MS = 60_000;

export const logPhotoHeadError = createConnectorLogger(PHOTO_HEAD_CONNECTOR, "PhotoHeadshotError");

/* ── Error copy — same createHttpClient(`Deckorate POST <path>: <status> <body>`)
 * parsing convention as fixAlignmentApi.ts's describeAlignmentFailure(). */

const UNAUTHORIZED_MESSAGE = "Your session has expired — sign in again and retry.";
const UPSTREAM_MESSAGE = "The face-detection service couldn't read this photo. Nothing was changed — try again.";
const SERVER_MESSAGE = "The photo headshot service is unavailable right now. Nothing was changed.";
export const TIMEOUT_MESSAGE = "The face-detection service took too long to respond. Nothing was changed — try again.";
export const CANCELLED_MESSAGE = "Creating the photo headshot was cancelled. Nothing was changed.";

function apiErrorMessage(rawBody: string): string | null {
  return parseJsonErrorBody(
    rawBody,
    (parsed) => {
      const body = parsed as { message?: unknown; error?: { message?: unknown } };
      if (typeof body.message === "string" && body.message) return body.message;
      const nested = body.error?.message;
      return typeof nested === "string" && nested ? nested : null;
    },
    logPhotoHeadError,
    "photo-head-error-body-parse",
  );
}

export function describePhotoHeadFailure(
  error: unknown,
  fallback: string,
  options: { timedOut?: boolean } = {},
): string {
  if (isAbortError(error)) return options.timedOut ? TIMEOUT_MESSAGE : CANCELLED_MESSAGE;

  const raw = getErrorMessage(error, fallback);
  const parsed = parseHttpError(raw);
  if (!parsed) return raw;

  const { status } = parsed;
  const apiMessage = apiErrorMessage(parsed.body);

  if (status === HTTP_STATUS.unauthorized || status === HTTP_STATUS.forbidden) return UNAUTHORIZED_MESSAGE;
  if (status >= HTTP_STATUS.badGateway) return apiMessage ? `${UPSTREAM_MESSAGE} (${apiMessage})` : UPSTREAM_MESSAGE;
  if (status >= HTTP_STATUS.serverError) return SERVER_MESSAGE;
  return apiMessage ?? raw;
}

export interface PhotoHeadBlobRef {
  readonly requestId: string;
  readonly blobContainer?: string;
  readonly blobName: string;
}

interface PhotoHeadDetectRequest {
  request_id: string;
  image_blob_container?: string;
  image_blob_name: string;
}

interface RawPhotoHeadDetectResponse {
  request_id?: unknown;
  found?: unknown;
  face_box?: unknown;
  message?: unknown;
}

export interface PhotoHeadDetectResult {
  readonly found: boolean;
  readonly faceBox: FaceBox | null;
  readonly message: string | null;
}

function toFaceBox(raw: unknown): FaceBox | null {
  if (typeof raw !== "object" || raw === null) return null;
  const box = raw as Record<string, unknown>;
  const { x, y, width, height } = box;
  if (
    typeof x !== "number" || typeof y !== "number" ||
    typeof width !== "number" || typeof height !== "number"
  ) {
    return null;
  }
  return { x, y, width, height };
}

function normalisePhotoHeadResult(raw: RawPhotoHeadDetectResponse | null | undefined): PhotoHeadDetectResult {
  const body = raw ?? {};
  return {
    found: body.found === true,
    faceBox: toFaceBox(body.face_box),
    message: typeof body.message === "string" ? body.message : null,
  };
}

/** POST the blob reference and return the parsed face-detection result. */
export async function postPhotoHeadDetect(
  http: HttpClient,
  ref: PhotoHeadBlobRef,
  options: HttpRequestOptions = {},
): Promise<PhotoHeadDetectResult> {
  const request: PhotoHeadDetectRequest = {
    request_id: ref.requestId,
    image_blob_name: ref.blobName,
    ...(ref.blobContainer ? { image_blob_container: ref.blobContainer } : {}),
  };
  const response = await http.post<RawPhotoHeadDetectResponse | null>(
    PHOTO_HEAD_DETECT_PATH,
    request,
    options,
  );
  return normalisePhotoHeadResult(response);
}
